import { useState, type ReactNode } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { LogOut, Monitor, Moon, Sun, UserRound } from 'lucide-react'

import { apiSend, errorMessage } from '@/lib/api'
import { can, roleLabel, type AuthConfig, type Me } from '@/lib/session'
import { getSessionLifecycle } from '@/lib/session-cache'
import { useTheme, type ThemeSetting } from '@/lib/theme'
import { DatabaseBadge } from '@/features/shell/database-badge'
import { NAV_GROUPS, PRODUCT } from '@/features/shell/nav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '@/components/ui/sidebar'

export function AppShell({ me, config, children }: { me: Me; config: AuthConfig; children: ReactNode }) {
  return (
    <SidebarProvider>
      <AppSidebar me={me} />
      <SidebarInset>
        <header className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur">
          <SidebarTrigger />
          <Separator orientation="vertical" className="mx-1 h-4" />
          <EnvironmentBadge environment={config.environment} />
          <DatabaseBadge environment={config.environment} />
          <div className="ml-auto flex items-center gap-1">
            <ThemeMenu />
            <AccountMenu me={me} />
          </div>
        </header>
        <main className="flex-1">{children}</main>
      </SidebarInset>
    </SidebarProvider>
  )
}

function AppSidebar({ me }: { me: Me }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild size="lg" tooltip="Wargard Testbench">
              <Link to="/">
                <span className="flex size-8 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground">
                  <PRODUCT.icon className="size-4" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-xs text-muted-foreground">Wargard</span>
                  <span className="font-semibold">{PRODUCT.name}</span>
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="sidebar-scrollbar">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((item) => !item.requires || can(me, item.requires))
          if (items.length === 0) return null
          return (
            <SidebarGroup key={group.label}>
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild tooltip={item.label} isActive={isActive(pathname, item.to)}>
                        <Link to={item.to}>
                          <item.icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )
        })}
      </SidebarContent>
      <SidebarFooter>
        <p className="px-2 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
          {me.name} · {roleLabel(me.role)}
        </p>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

function isActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`)
}

/** Makes the environment impossible to miss: anything that is not Production is labelled. */
function EnvironmentBadge({ environment }: { environment: string }) {
  if (environment === 'Production') return null
  return (
    <Badge variant="outline" className="font-mono text-[11px] uppercase">
      {environment}
    </Badge>
  )
}

const THEMES: { value: ThemeSetting; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

function ThemeMenu() {
  const { theme, resolved, setTheme } = useTheme()
  const Icon = resolved === 'dark' ? Moon : Sun
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Theme">
          <Icon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemeSetting)}>
          {THEMES.map((t) => (
            <DropdownMenuRadioItem key={t.value} value={t.value}>
              <t.icon aria-hidden /> {t.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function AccountMenu({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const signOut = async () => {
    setBusy(true)
    setError(null)
    try {
      await apiSend('/auth/signout', 'POST')
      await getSessionLifecycle(queryClient).recheck()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Account">
          <UserRound />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span>{me.name}</span>
          <span className="text-xs font-normal text-muted-foreground">
            {me.email ?? me.kind} · {roleLabel(me.role)}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings">Access &amp; MCP</Link>
        </DropdownMenuItem>
        {(me.kind === 'user' || me.kind === 'shared') && (
          <DropdownMenuItem disabled={busy} onSelect={(event) => { event.preventDefault(); void signOut() }}>
            <LogOut aria-hidden /> {busy ? 'Signing out…' : 'Sign out'}
          </DropdownMenuItem>
        )}
        {error && <DropdownMenuLabel role="alert" className="max-w-72 text-sm font-normal text-destructive">{error}</DropdownMenuLabel>}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
